// @ts-expect-error TS(7016) - no type declaration
import makeSubject from "callbag-subject";
import { push } from "connected-react-router";
import { isEmpty, pickBy } from "lodash";
import { SagaIterator } from "redux-saga";
import { call, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";

import { Filter } from "constants/filters";
import Role from "constants/role";
import { TicketsRoutes, getPathForRoute } from "constants/tickets/TicketsRoutes";
import { TicketsBatchAction } from "constants/tickets/batch-action";
import { SESSION_STORAGE_DRAFT_KEY } from "constants/tickets/drafts";
import { TICKET_PRIORITY_LABEL_MAP } from "constants/tickets/priorities";
import { IAssigneeUpdateToastPayload, ToastContent, getToastContent } from "constants/toasts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { pushWithUTM } from "helpers/sagas";
import { getFilterValuesForRoute, getIsMemberOfTicketTeam, getRouteByPathname } from "helpers/tickets";
import { ITicket, TicketSilo } from "interfaces/entities/ticket";
import { ApiManager } from "services/api";
import { BrowserHistory } from "services/browser-history";
import { getItem, saveItem } from "services/session-storage";
import {
  CRUDAction,
  IEntityUpdateFailure,
  IRequestError,
  IRequestErrorWithDetails,
  RequestAction,
} from "store/entities/actions";
import { IAgent, selectAgent, selectLoggedInAgent, selectLoggedInAgentPermission } from "store/entities/agents";
import { CustomViewsActions } from "store/entities/custom-views/actions";
import { ICustomView } from "store/entities/custom-views/interfaces";
import { selectCustomViews } from "store/entities/custom-views/selectors";
import { TICKET, TicketActions } from "store/entities/tickets/actions";
import {
  ICreateTicketFailurePayload,
  IFetchClosestTicketInfoPayload,
  IFetchClosestTicketSuccessPayload,
  ISendTicketMessageFailurePayload,
} from "store/entities/tickets/interfaces";
import { EventTrackingActions } from "store/features/event-tracking/actions";
import { getCurrentAgentId } from "store/features/session";
import { ToastsActions } from "store/features/toasts/actions";
import { ToastAutoHideDelay, ToastVariant } from "store/features/toasts/interfaces";
import { IAction, IActionWithPayload } from "store/helper";
import { TicketsViewActions, TicketsViewActionsNames } from "store/views/tickets/actions";

import {
  IFetchTicketsAction,
  IMergeTicketsPayload,
  IPerformBulkActionPayload,
  ITicketsViewAddFollowerPayload,
  ITicketsViewBatchUpdatePayload,
  ITicketsViewBatchUpdateSuccessPayload,
  ITicketsViewClearDraftPayload,
  ITicketsViewCreateTicketPayload,
  ITicketsViewDeleteFollowerPayload,
  ITicketsViewDeleteTicketPayload,
  ITicketsViewFetchOriginalMessage,
  ITicketsViewFetchOriginalMessageFailure,
  ITicketsViewFetchUnstrippedMessage,
  ITicketsViewSaveDraftPayload,
  ITicketsViewSelectCustomViewPayload,
  ITicketsViewSelectStandardViewPayload,
  ITicketsViewSendMessagePayload,
  ITicketsViewSendMessageSuccessPayload,
  ITicketsViewSetQueryParams,
  ITicketsViewSetRoutePayload,
  ITicketsViewSetViewPayload,
  ITicketsViewUnspamPayload,
  ITicketsViewUpdateAssignedTeamPayload,
  ITicketsViewUpdateAssigneePayload,
  ITicketsViewUpdateCCsPayload,
  ITicketsViewUpdateFilterPayload,
  ITicketsViewUpdatePriorityPayload,
  ITicketsViewUpdateRequesterPayload,
  ITicketsViewUpdateStatusPayload,
  ITicketsViewUpdateSubjectPayload,
  ITicketsViewUpdateTagsPayload,
} from "./interfaces";
import {
  getActiveFilters,
  getCurrentPage,
  getDataFetchedForRoute,
  getFilters,
  getFiltersOrder,
  getRoute,
  getSearchQuery,
  getSilo,
  getTicket,
  getTickets,
  getTicketsData,
  selectActiveCustomViewId,
  selectCanManageTickets,
  selectCurrentSorting,
  selectRecentTicketsAvailable,
} from "./selectors";

/**
 * @param action An action that invokes fetchTickets. Note that there are many
 *   possible actions that invoke it - see ticketsViewSagas() to find these actions
 */

export function* fetchTickets(action: IFetchTicketsAction): SagaIterator {
  const route = yield select(getRoute);
  const filters = yield select(getFilters);
  const query = yield select(getSearchQuery);
  const page = yield select(getCurrentPage);
  const silo = yield select(getSilo);
  const tickets = yield select(getTicketsData);
  const currentSorting = yield select(selectCurrentSorting);

  const dataFetchedForRoute = yield select(getDataFetchedForRoute);
  if (
    dataFetchedForRoute === route &&
    !isEmpty(tickets) &&
    action.type !== TicketsViewActionsNames.PERFORM_BULK_SILO_CHANGE_DONE &&
    action.type !== TicketsViewActionsNames.SET_SILO &&
    action.type !== TicketsViewActionsNames.UPDATE_CURRENT_SORTING &&
    action.type !== TicketsViewActionsNames.RESET_CURRENT_SORTING
  ) {
    return;
  }

  if (query.length === 0) {
    yield put(
      TicketActions.fetchCollection({
        query,
        page,
        filters,
        silo,
        hasParent: false,
        sorting: currentSorting,
      })
    );
  } else if (route) {
    yield put(
      TicketActions.fetchCollection({
        query,
        page,
        filters,
        silo,
        sorting: currentSorting,
      })
    );
  }
}

export function* searchQueryChanged(action: IActionWithPayload<string, { query: string }>): SagaIterator {
  const { query } = action.payload;
  const route = yield select(getRoute);
  const silo = yield select(getSilo);

  if (query.length === 0 && route === TicketsRoutes.Search) {
    yield put(
      TicketsViewActions.setView({
        route: TicketsRoutes.All,
        query,
        silo,
        filters: {},
      })
    );
  } else if (query.length > 0) {
    yield put(
      TicketsViewActions.setView({
        route: TicketsRoutes.Search,
        query,
        silo,
        filters: {},
      })
    );
    if (route !== TicketsRoutes.Search) {
      yield put(pushWithUTM(getPathForRoute(TicketsRoutes.Search)));
      yield put(EventTrackingActions.trackEvent("Search used", EventPlace.Tickets));
    }

    yield put(TicketsViewActions.setFocusedColumn(1));
  }
}

export function* searchCleared(): SagaIterator {
  const currentRoute = getRouteByPathname(BrowserHistory.pathname);
  if (currentRoute !== TicketsRoutes.All) {
    yield put(pushWithUTM(`/tickets/${TicketsRoutes.All}`));
  }
}

export function* saveDraft(action: IActionWithPayload<string, ITicketsViewSaveDraftPayload>): SagaIterator {
  const {
    ticketId,
    message,
    isPrivate,
    status,
    subject,
    requesterName,
    requesterEmail,
    assigneeId,
    assignedTeamId,
    priority,
    tags,
    cannedResponseIds,
    peopleInTheLoop,
  } = action.payload;

  let ticketDrafts = yield call(getItem, SESSION_STORAGE_DRAFT_KEY);
  if (!ticketDrafts) {
    ticketDrafts = {};
  }
  ticketDrafts[ticketId] = {
    message,
    isPrivate,
    status,
    subject,
    requesterName,
    requesterEmail,
    assigneeId,
    assignedTeamId,
    priority,
    tags,
    cannedResponseIds,
    peopleInTheLoop,
  };

  yield call(saveItem, SESSION_STORAGE_DRAFT_KEY, ticketDrafts);
}

export function* clearDraft(action: IActionWithPayload<string, ITicketsViewClearDraftPayload>): SagaIterator {
  const { ticketId } = action.payload;

  const ticketDrafts = yield call(getItem, SESSION_STORAGE_DRAFT_KEY);
  if (ticketDrafts) {
    delete ticketDrafts[ticketId];
    yield call(saveItem, SESSION_STORAGE_DRAFT_KEY, ticketDrafts);
  }
}

export function* unspam(action: IActionWithPayload<string, ITicketsViewUnspamPayload>): SagaIterator {
  yield put(TicketActions.unspam(action.payload));
}

export function* copyTicketUrl(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_COPY_URL_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
  yield put(EventTrackingActions.trackEvent("URL copied", EventPlace.Tickets));
}

export function* copyRequesterEmail(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_COPY_REQUESTER_EMAIL_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

export function* setCurrentItem(): SagaIterator {
  yield put(TicketsViewActions.setFocusedColumn(0));
}

export function* showFromAllTeams(): SagaIterator {
  yield put(pushWithUTM(getPathForRoute(TicketsRoutes.All)));
  yield put(EventTrackingActions.trackEvent("See tickets from all teams", EventPlace.Tickets));
}

export function* setInitialRoute(action: IActionWithPayload<string, ITicketsViewSetRoutePayload>): SagaIterator {
  const { route } = action.payload;
  const searchQuery = yield select(getSearchQuery);
  if (route !== TicketsRoutes.Search && searchQuery.length > 0) {
    yield put(TicketsViewActions.setSearchQuery(""));
  }
}

export function* selectCustomView(
  action: IActionWithPayload<string, ITicketsViewSelectCustomViewPayload>
): SagaIterator {
  const { id } = action.payload;
  const { filters } = (yield select(selectCustomViews)).data[id];
  yield put(TicketsViewActions.setView({ route: TicketsRoutes.All, activeCustomViewId: id, filters }));
}

export function* selectStandardView(
  action: IActionWithPayload<string, ITicketsViewSelectStandardViewPayload>
): SagaIterator {
  const { route } = action.payload;
  const currentAgentId = yield select(getCurrentAgentId);
  const filters = getFilterValuesForRoute(route, { currentAgentId });

  // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string | un... Remove this comment to see the full error message
  yield put(TicketsViewActions.setView({ route, activeCustomViewId: null, filters }));
}

export function* fetchSingle(
  action: IActionWithPayload<string, { id: string; overwriteCurrent: boolean }>
): SagaIterator {
  const { id, overwriteCurrent } = action.payload;
  yield put(TicketActions.fetch({ id, overwriteCurrent }));
}

export function* ticketFetchFailed(): SagaIterator {
  yield put(pushWithUTM("/tickets"));
}

export function* fetchClosestTicket(action: IActionWithPayload<string, IFetchClosestTicketInfoPayload>): SagaIterator {
  const filters = yield select(getFilters);
  const query = yield select(getSearchQuery);
  const silo = yield select(getSilo);
  const currentSorting = yield select(selectCurrentSorting);

  yield put(
    TicketActions.fetchClosestTicket({
      params: {
        filters,
        query,
        silo,
        hasParent: false,
        sorting: currentSorting,
      },
      ticketInfo: action.payload,
    })
  );
}

export function* fetchClosestTicketSuccess(
  action: IActionWithPayload<string, IFetchClosestTicketSuccessPayload>
): SagaIterator {
  const { ticket, ticketInfo } = action.payload;
  const { direction } = ticketInfo;
  if (!ticket) {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(
          direction === "next" ? ToastContent.FETCH_NEXT_TICKET_FAILED : ToastContent.FETCH_PREV_TICKET_FAILED
        ),
        variant: ToastVariant.Success,
      })
    );
  } else {
    yield put(push(`/tickets/${ticket.id}`));
    yield put(TicketsViewActions.setCurrentItem(ticket));
  }
}

export function* ticketUpdated(
  action: IActionWithPayload<string, { ticketId: string; action: string; ticket: ITicket }>
): SagaIterator {
  const { ticketId, action: messageAction, ticket } = action.payload;
  const currentTicket = yield select(getTicket);
  const agent = yield select(selectLoggedInAgent);

  if (ticket) {
    yield put(
      TicketActions.fetchSuccess({
        id: ticketId,
        value: ticket,
        agent,
        incrementCounter: messageAction === "create",
        overwriteCurrent: currentTicket?.id === ticket.id,
      })
    );
  } else {
    yield put(TicketActions.fetch({ id: ticketId, incrementCounter: messageAction === "create" }));
  }
}

export function* updateFilter(action: IActionWithPayload<string, ITicketsViewUpdateFilterPayload>): SagaIterator {
  const filtersOrder: Filter[] = yield select(getFiltersOrder);
  const filters = yield select(getFilters);

  const filtersWithoutSilo = pickBy(filters, (_, filterName) => filterName !== Filter.Silo);
  const concurrentFilters = filtersOrder.filter(filter => filter !== Filter.Silo);
  const { name, applyState } = action.payload;
  const otherAppliedFilters = concurrentFilters.filter(filter => filter !== name);

  yield put(
    EventTrackingActions.trackEvent("Filter applied", EventPlace.Tickets, {
      filter_type: name,
      concurrent_filters: concurrentFilters.length,
      multiSelect: Object.values(filtersWithoutSilo).some(filter => Array.isArray(filter) && filter.length > 1),
      silo: filters.silo ?? TicketSilo.Tickets,
      other_selected_filters: otherAppliedFilters,
      ...(applyState ? { state: applyState } : {}),
    })
  );
}

export function* setQueryParams(action: IActionWithPayload<string, ITicketsViewSetQueryParams>): SagaIterator {
  const { query, page, route, silo: urlSilo, order, sortBy, ...filters } = action.payload.params;

  if (!route) {
    return;
  }

  // @ts-expect-error TS(2769)
  const validFilters = pickBy(filters, (_, filterName: Filter) => Object.values(Filter).includes(filterName));
  const currentAgentId = yield select(getCurrentAgentId);
  const defaultSilo = query ? TicketSilo.Tickets : yield select(getSilo);
  const silo = urlSilo || defaultSilo;
  const filtersWithRouteDefaults = {
    ...validFilters,
    ...getFilterValuesForRoute(route, { currentAgentId }),
  };

  yield put(
    TicketsViewActions.setView({
      route,
      silo,
      filters: filtersWithRouteDefaults,
      query,
      page,
      order,
      sortBy,
    })
  );
}

export function* setView(action: IActionWithPayload<string, ITicketsViewSetViewPayload>): SagaIterator {
  yield put(
    EventTrackingActions.trackEvent("Multiple filters applied", EventPlace.Tickets, {
      concurrent_filters: action.payload.filters,
    })
  );
  const currentRoute = getRouteByPathname(BrowserHistory.pathname);
  const { route } = action.payload;
  if (currentRoute !== route) {
    yield put(pushWithUTM(`/tickets/${route}`));
  }
}

export function* updateTags(action: IActionWithPayload<string, ITicketsViewUpdateTagsPayload>): SagaIterator {
  yield put(TicketActions.updateTags({ ...action.payload }));
}

export function* updatePriority(action: IActionWithPayload<string, ITicketsViewUpdatePriorityPayload>): SagaIterator {
  const { ticketId, priority, interaction } = action.payload;
  const tickets = yield select(getTickets);
  const currentTicket = yield select(getTicket);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ticket = currentTicket?.id === ticketId ? currentTicket : tickets.find(({ id }: any) => id === ticketId);

  if (ticket) {
    yield put(TicketActions.updatePriority({ ticketId, priority }));
    yield put(
      EventTrackingActions.trackEvent("Priority: Ticket priority changed", EventPlace.Tickets, {
        ticketId,
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        current: TICKET_PRIORITY_LABEL_MAP[ticket.priority],
        next: TICKET_PRIORITY_LABEL_MAP[priority],
        interaction,
      })
    );
  }
}

export function* updateStatus(action: IActionWithPayload<string, ITicketsViewUpdateStatusPayload>): SagaIterator {
  const { ticketId, status, interaction, usingShortcut } = action.payload;
  const tickets = yield select(getTickets);
  const currentTicket = yield select(getTicket);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ticket = currentTicket?.id === ticketId ? currentTicket : tickets.find(({ id }: any) => id === ticketId);

  if (ticket) {
    yield put(TicketActions.updateStatus({ ticketId, status }));
    yield put(
      EventTrackingActions.trackEvent("Status: Ticket status changed", EventPlace.Tickets, {
        ticketId,
        current: ticket.status,
        next: status,
        interaction,
        method: usingShortcut && "shortcut",
      })
    );
  }
}

export function* updateSubject(action: IActionWithPayload<string, ITicketsViewUpdateSubjectPayload>): SagaIterator {
  yield put(TicketActions.updateSubject(action.payload));
  yield put(EventTrackingActions.trackEvent("Subject changed", EventPlace.Tickets));
}

export function* updateRequester(action: IActionWithPayload<string, ITicketsViewUpdateRequesterPayload>): SagaIterator {
  yield put(TicketActions.updateRequester(action.payload));
  yield put(EventTrackingActions.trackEvent("Requester changed", EventPlace.Tickets));
}

export function* updateCCs(action: IActionWithPayload<string, ITicketsViewUpdateCCsPayload>): SagaIterator {
  const { payload } = action;
  yield put(TicketActions.updateCCs(payload));
  yield put(
    EventTrackingActions.trackEvent("CCs changed", EventPlace.Tickets, {
      "CCs total": payload.ccs.length,
    })
  );
}

export function* updateAssignee(action: IActionWithPayload<string, ITicketsViewUpdateAssigneePayload>): SagaIterator {
  yield put(TicketActions.updateAssignee(action.payload));
  yield put(EventTrackingActions.trackEvent("Assignee changed", EventPlace.Tickets));
}

export function* updateAssignedTeam(
  action: IActionWithPayload<string, ITicketsViewUpdateAssignedTeamPayload>
): SagaIterator {
  const { payload } = action;
  yield put(TicketActions.updateAssignedTeam(payload));
  yield put(EventTrackingActions.trackEvent("Assigned team changed", EventPlace.Tickets));
}

export function* updateTeamsSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_TEAMS_UPDATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

export function* updateTeamsFailure(action: IActionWithPayload<string, { error: IRequestError }>): SagaIterator {
  const { error } = action.payload;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_TEAMS_UPDATE_ERROR),
      variant: ToastVariant.Error,
      error,
    })
  );
}

export function* updateSubjectSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_SUBJECT_UPDATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

export function* updateRequesterSuccess(action: IActionWithPayload<string, ITicket>): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_REQUESTER_UPDATE_SUCCESS, action.payload),
      variant: ToastVariant.Success,
    })
  );
}

export function* updateAssigneeSuccess(
  action: IActionWithPayload<string, { ticket: ITicket; agent: IAgent }>
): SagaIterator {
  const { ticket } = action.payload;
  const toastPayload: IAssigneeUpdateToastPayload = {};
  if (ticket.assignment.agent) {
    const agent = yield select(selectAgent, ticket.assignment.agent.id);
    toastPayload.assignee = { name: agent.name };
  }

  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_ASSIGNEE_UPDATE_SUCCESS, toastPayload),
      variant: ToastVariant.Success,
    })
  );
}

export function* updateCCsSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_CCS_UPDATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

export function* updateRequesterFailure(
  action: IActionWithPayload<string, { error: IRequestErrorWithDetails }>
): SagaIterator {
  const { error } = action.payload;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_REQUESTER_UPDATE_ERROR, { error }),
      variant: ToastVariant.Error,
    })
  );
}

export function* updateCustomFieldSuccess(): SagaIterator {
  yield call(trackEngagement, "Custom fields: Value saved in the ticket", EventPlace.Tickets);
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.CUSTOM_FIELD_UPDATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
export function* updateCustomFieldFailure(action: IActionWithPayload<string, { error: IRequestError }>): SagaIterator {
  const { error } = action.payload;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.CUSTOM_FIELD_UPDATE_FAILURE),
      variant: ToastVariant.Error,
      error,
    })
  );
}

export function* updateSiloSuccess(action: IActionWithPayload<string, TicketSilo>): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_SILO_UPDATE_SUCCESS, { silo: action.payload }),
      variant: ToastVariant.Success,
    })
  );
}

export function* updateSiloFailure(
  action: IActionWithPayload<string, { silo: TicketSilo; error: IRequestError }>
): SagaIterator {
  const { error } = action.payload;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_SILO_UPDATE_FAILURE, { silo: action.payload?.silo }),
      variant: ToastVariant.Error,
      error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
export function* updateCCsFailure(action: IActionWithPayload<string, { error: IRequestError }>): SagaIterator {
  const { error } = action.payload;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_CCS_UPDATE_ERROR),
      variant: ToastVariant.Error,
      error,
    })
  );
}

export function* updateAssigneeFailure(action: IActionWithPayload<string, { error: IRequestError }>): SagaIterator {
  const { error } = action.payload;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_ASSIGNEE_UPDATE_ERROR),
      variant: ToastVariant.Error,
      error,
    })
  );
}

export function* updatePrioritySuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_PRIORITY_UPDATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

export function* updatePriorityFailure(action: IActionWithPayload<string, { error: IRequestError }>): SagaIterator {
  const { error } = action.payload;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_PRIORITY_UPDATE_ERROR),
      variant: ToastVariant.Error,
      error,
    })
  );
}

export function* summarizeTicketSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_SUMMARIZE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

export function* summarizeTicketFailure(
  action: IActionWithPayload<string, { error: IRequestError; ticketId: string }>
): SagaIterator {
  const { error } = action.payload;

  if (error?.message !== "No new events to summarize") {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.TICKET_SUMMARIZE_ERROR),
        variant: ToastVariant.Error,
        error,
      })
    );
  }
}

export function* applyMacroSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.APPLY_MACRO_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

export function* applyMacroFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.APPLY_MACRO_ERROR),
      variant: ToastVariant.Error,
    })
  );
}

export function* updateStatusSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_STATUS_UPDATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

export function* updateStatusFailure(action: IActionWithPayload<string, { error: IRequestError }>): SagaIterator {
  const { error } = action.payload;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_STATUS_UPDATE_ERROR),
      variant: ToastVariant.Error,
      error,
    })
  );
}

export function* createTicket(action: IActionWithPayload<string, ITicketsViewCreateTicketPayload>): SagaIterator {
  const payload = { ...action.payload };
  // @ts-expect-error TS(2345): Argument of type '{ subject: string; requester: IT... Remove this comment to see the full error message
  yield put(TicketActions.createTicket(payload));
}

export function* createTicketSuccess(action: IActionWithPayload<string, ITicket>): SagaIterator {
  const { payload } = action;

  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.CREATE_TICKET_SUCCESS),
      variant: ToastVariant.Success,
    })
  );

  trackEngagement("Ticket created manually", EventPlace.Tickets, {
    "Is assigned": payload.assignment.agent ? "Yes" : "No",
    "People in the loop": payload.ccs?.length ?? 0,
    priority: TICKET_PRIORITY_LABEL_MAP[payload.priority],
    status: payload.status,
    tags: payload.tagIds?.length ?? 0,
  });
  yield put(pushWithUTM(`/tickets/${payload.id}`));
}

export function* createTicketFailure(action: IActionWithPayload<string, ICreateTicketFailurePayload>): SagaIterator {
  const { type, error, details } = action.payload;

  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.CREATE_TICKET_ERROR, { type, error, details }),
      variant: ToastVariant.Error,
      autoHideDelayTime: type === "limitExceeded" ? ToastAutoHideDelay.Infinite : ToastAutoHideDelay.Long,
      // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      error: { message: error, type },
    })
  );
}

export function* deleteTicket(action: IActionWithPayload<string, ITicketsViewDeleteTicketPayload>): SagaIterator {
  const payload = { ...action.payload };
  yield put(TicketActions.deleteTicket(payload));
}

export function* deleteTicketSuccess(action: IActionWithPayload<string, { shouldRedirect: boolean }>): SagaIterator {
  const { shouldRedirect } = action.payload;

  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.DELETE_TICKET_SUCCESS),
      variant: ToastVariant.Success,
    })
  );

  if (shouldRedirect) {
    const lastRoute = yield select(getRoute);
    yield put(pushWithUTM(`/tickets/${lastRoute || ""}`));
  }
}

export function* deleteTicketFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.DELETE_TICKET_ERROR),
      variant: ToastVariant.Error,
    })
  );
}

export function* sendMessage(action: IActionWithPayload<string, ITicketsViewSendMessagePayload>): SagaIterator {
  const {
    payload: { mentionedAgentIDs, usingShortcut, ...payload },
  } = action;
  const { isPrivate } = payload;
  yield put(TicketActions.sendMessage(payload));

  if (isPrivate && mentionedAgentIDs && mentionedAgentIDs.length > 0) {
    const resultAction: IAction = yield take([
      TICKET.SEND_MESSAGE[RequestAction.SUCCESS],
      TICKET.SEND_MESSAGE[RequestAction.FAILURE],
    ]);
    if (resultAction.type === TICKET.SEND_MESSAGE[RequestAction.SUCCESS]) {
      const { ticketId } = action.payload;
      yield call(ApiManager.ticketApi.addFollowers, ticketId, mentionedAgentIDs);
    }
    yield put(
      EventTrackingActions.trackEvent("Mentions: Agents mentioned in message", EventPlace.Tickets, {
        agentsCount: mentionedAgentIDs.length,
      })
    );
  }

  yield put(
    EventTrackingActions.trackEvent("Ticket replied", EventPlace.Tickets, {
      Status: payload.status,
      "Is private": payload.isPrivate,
      method: usingShortcut ? "shortcut" : "button",
      ...(payload.attachments ? { Attachments: payload.attachments.length } : { TransactionID: payload.transactionID }),
    })
  );
}

export function* sendMessageSuccess(
  action: IActionWithPayload<string, ITicketsViewSendMessageSuccessPayload>
): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_SEND_MESSAGE_SUCCESS, action.payload.originalRequest),
      variant: ToastVariant.Success,
    })
  );

  action.payload.originalRequest.handleClearMessageBox?.();
  const { ticket, agent } = action.payload;
  const canManageTicket = yield select(selectCanManageTickets);
  const isChildTicket = !!(ticket && ticket.parentTicket);
  const isRecentTicket = ticket?.silo === TicketSilo.Tickets;
  const isMemberOfTicketTeam = ticket && getIsMemberOfTicketTeam(agent, ticket);

  const refetchTagSuggestions = !isChildTicket && canManageTicket && isRecentTicket && isMemberOfTicketTeam;

  if (refetchTagSuggestions) {
    yield put(TicketActions.fetchTagSuggestions({ ticketId: action.payload.ticket.id }));
  }
}

export function* sendMessageFailure(
  action: IActionWithPayload<string, ISendTicketMessageFailurePayload>
): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_SEND_MESSAGE_ERROR, {
        reason: action.payload.reason,
        error: action.payload.error,
      }),
      variant: ToastVariant.Error,
      autoHideDelayTime:
        action.payload.reason === "limitExceeded" ? ToastAutoHideDelay.Infinite : ToastAutoHideDelay.Long,
      // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      error: { message: action.payload.error, type: action.payload.reason },
    })
  );
}

export function* unspamSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_UNSPAM_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

export function* unspamFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_UNSPAM_ERROR),
      variant: ToastVariant.Error,
    })
  );
}

const batchActionToPropertyMap = {
  [TicketsBatchAction.MarkAsOpen]: "status",
  [TicketsBatchAction.MarkAsSolved]: "status",
  [TicketsBatchAction.MarkAsClosed]: "status",
  [TicketsBatchAction.ChangeAssignee]: "assignee",
  [TicketsBatchAction.ChangeTeam]: "team",
};

// FIXME: Fix this the next time the file is edited.
export function* updateBatch(action: IActionWithPayload<string, ITicketsViewBatchUpdatePayload>): SagaIterator {
  const { ticketIds, batchAction, data } = action.payload;

  const response = yield call(ApiManager.ticketApi.batch, ticketIds, batchAction, data);

  if (response.result) {
    yield put(TicketsViewActions.updateBatchSuccess({ ticketIds, batchAction, data }));
  } else {
    yield put(TicketsViewActions.updateBatchFailure({ error: response.error.error }));
  }
  yield put(
    EventTrackingActions.trackEvent("Many tickets edited", EventPlace.Tickets, {
      "Tickets edited": ticketIds.length,
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      Property: batchActionToPropertyMap[batchAction],
    })
  );
}

// FIXME: Fix this the next time the file is edited.
export function* updateBatchSuccess(
  action: IActionWithPayload<string, ITicketsViewBatchUpdateSuccessPayload>
): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_BATCH_UPDATE_SUCCESS, action.payload),
      variant: ToastVariant.Success,
    })
  );
}

export function* updateBatchFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_BATCH_UPDATE_ERROR),
      variant: ToastVariant.Error,
    })
  );
}

export function* updateCustomView(): SagaIterator {
  const activeCustomViewId = yield select(selectActiveCustomViewId);

  const activeCustomView: ICustomView = (yield select(selectCustomViews)).data[activeCustomViewId];
  if (activeCustomViewId && activeCustomView) {
    const activeFilters = yield select(getActiveFilters);
    const customView = {
      ...activeCustomView,
      filters: activeFilters,
    };

    yield put(CustomViewsActions.update({ value: customView }));
    yield call(trackEngagement, "Custom views: Custom view updated", EventPlace.CustomViews, {
      shared: activeCustomView.agentID === null,
    });
  }
}

export function* createCustomView(
  action: IActionWithPayload<string, { name: string; shared?: boolean }>
): SagaIterator {
  const { name, shared } = action.payload;

  const isAdmin = (yield select(selectLoggedInAgentPermission)) === Role.Owner;

  const activeFilters = yield select(getActiveFilters);
  const customView = {
    name,
    filters: activeFilters,
    ...(isAdmin ? { shared } : {}),
  };

  yield put(CustomViewsActions.create({ value: customView }));
  yield call(trackEngagement, "Custom views: Custom view created", EventPlace.CustomViews, { shared });
}

export function* fetchUnstrippedMessage(
  action: IActionWithPayload<string, ITicketsViewFetchUnstrippedMessage>
): SagaIterator {
  const { eventId } = action.payload;

  const ticket = yield select(getTicket);

  const response = yield call(ApiManager.ticketApi.fetchUnstrippedMessage, ticket.id, eventId);

  if (response.error) {
    yield put(
      TicketsViewActions.fetchUnstrippedMessageFailure({
        ticketId: ticket.id,
        id: eventId,
        error: response.error,
      })
    );
  }

  yield put(
    TicketsViewActions.fetchUnstrippedMessageSuccess({
      ticketId: ticket.id,
      eventId,
      message: response.result.message,
    })
  );
}

export function* fetchUnstrippedMessageFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_FETCH_UNSTRIPPED_MESSAGE_FAILED),
      variant: ToastVariant.Error,
    })
  );
}

export function* fetchOriginalMessage(
  action: IActionWithPayload<string, ITicketsViewFetchOriginalMessage>
): SagaIterator {
  const { eventId } = action.payload;

  const ticket = yield select(getTicket);

  const response = yield call(ApiManager.ticketApi.fetchOriginalMessage, ticket.id, eventId);
  if (response.error) {
    yield put(
      TicketsViewActions.fetchOriginalMessageFailure({
        ticketId: ticket.id,
        id: eventId,
        error: response.error,
      })
    );
  } else {
    yield put(
      TicketsViewActions.fetchOriginalMessageSuccess({
        ticketId: ticket.id,
        eventId,
        message: response.result.message,
      })
    );
  }
}

export function* fetchOriginalMessageFailure(
  action: IActionWithPayload<string, ITicketsViewFetchOriginalMessageFailure>
): SagaIterator {
  const ticket: ITicket = yield select(getTicket);
  const event = ticket.events.find(_event => _event.id === action.payload.id);

  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const minutesElapsed = Math.round(Math.abs((event.date.getTime() - new Date().getTime()) / (60 * 1000)));

  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TICKET_FETCH_ORIGINAL_MESSAGE_FAILED, { minutesElapsed }),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
export function* syncDelete(action: IActionWithPayload<string, { id: string }>): SagaIterator {
  yield put(TicketActions.deleteSuccess({ id: action.payload.id, requestSource: "websocket" }));
}

export function* deleteFollower(action: IActionWithPayload<string, ITicketsViewDeleteFollowerPayload>): SagaIterator {
  const { ticketID, agentID } = action.payload;

  const response = yield call(ApiManager.ticketApi.deleteFollower, ticketID, agentID);

  if (response.result) {
    yield put(TicketsViewActions.deleteFollowerSuccess());
  } else {
    yield put(TicketsViewActions.deleteFollowerFailure({ id: ticketID }));
  }
}

export function* addFollower(action: IActionWithPayload<string, ITicketsViewAddFollowerPayload>): SagaIterator {
  const { agentID, ticketID } = action.payload;

  const response = yield call(ApiManager.ticketApi.addFollowers, ticketID, [agentID]);

  if (response.result) {
    yield put(TicketsViewActions.addFollowerSuccess());
  } else {
    yield put(TicketsViewActions.addFollowerFailure({ id: ticketID, error: response.error }));
  }
}

export function* addFollowerSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.ADD_FOLLOWER_SUCCESS),
    })
  );
}

// FIXME: Fix this the next time the file is edited.
export function* addFollowerFailure(action: IActionWithPayload<string, IEntityUpdateFailure>): SagaIterator {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Error,
      content: getToastContent(ToastContent.ADD_FOLLOWER_FAILURE, {
        type: (
          action.payload.error as unknown as {
            error: { type: string; message: string };
          }
        )?.error?.type,
      }),
    })
  );
}

export function* deleteFollowerSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.DELETE_FOLLOWER_SUCCESS),
    })
  );
}

// FIXME: Fix this the next time the file is edited.
export function* deleteFollowerFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Error,
      content: getToastContent(ToastContent.DELETE_FOLLOWER_FAILURE),
    })
  );
}

export function* ticketTagLimitReachedFailure(
  action: IActionWithPayload<string, { error: { type: string; message: string } }>
): SagaIterator {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Error,
      content: getToastContent(ToastContent.REPORT_TOO_MUCH_TAGS_IN_TICKET),
      error: action.payload?.error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
export function* performBulkAction(action: IActionWithPayload<string, IPerformBulkActionPayload>): SagaIterator {
  const { selectedIds, pendingActions } = action.payload;
  const tickets = yield select(getTickets);

  const updateCalls = [];
  const progressChannel = makeSubject();

  yield put(
    ToastsActions.createToast({
      id: "BULK_ACTION_PROGRESS_TOAST",
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.BULK_ACTION_PROGRESS, { progressChannel, count: selectedIds.length }),
      autoHideDelayTime: ToastAutoHideDelay.Infinite,
      removable: false,
    })
  );

  for (let i = 0; i < selectedIds.length; i += 1) {
    const ticketId = selectedIds[i];
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ticket = tickets.find((t: any) => t.id === ticketId);

    /* For parallel processing:
    updateCalls.push(
      yield fork(function* () {
        const response = yield call(
          ApiManager.ticketApi.updatePendingActions,
          ticketId,
          pendingActions,
          ticket.teamIds,
          ticket.tagIds
        );

        progressChannel.put({});
        return response;
      })
    );
    */

    const response = yield call(
      ApiManager.ticketApi.updatePendingActions,
      ticketId,
      pendingActions,
      ticket.teamIds,
      ticket.tagIds
    );
    progressChannel(1, {});
    updateCalls.push(response);
  }

  /* For parallel processing:
  const callResults = yield join(updateCalls);
  */
  const callResults = updateCalls;
  progressChannel(2);
  yield put(ToastsActions.removeToast({ id: "BULK_ACTION_PROGRESS_TOAST" }));

  const successCount = callResults.filter(r => r.result).length;
  const failureCount = callResults.filter(r => r.error).length;

  yield put(TicketsViewActions.performBulkActionDone(successCount, failureCount));
}

export function* performBulkSiloChange(
  action: IActionWithPayload<string, { selectedIds: string[]; newSilo: TicketSilo }>
): SagaIterator {
  const { selectedIds, newSilo } = action.payload;
  const siloChangeCalls = [];
  const progressChannel = makeSubject();

  yield put(
    ToastsActions.createToast({
      id: "BULK_SILO_CHANGE_PROGRESS_TOAST",
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.BULK_ACTION_PROGRESS, { progressChannel, count: selectedIds.length }),
      autoHideDelayTime: ToastAutoHideDelay.Infinite,
      removable: false,
    })
  );

  for (let i = 0; i < selectedIds.length; i += 1) {
    const ticketId = selectedIds[i];

    const response = yield call(ApiManager.ticketApi.updateSilo, ticketId, { silo: newSilo });
    progressChannel(1, {});
    siloChangeCalls.push(response);
  }

  const callResults = siloChangeCalls;
  progressChannel(2);
  yield put(ToastsActions.removeToast({ id: "BULK_SILO_CHANGE_PROGRESS_TOAST" }));

  const successCount = callResults.filter(r => r.result).length;
  const failureCount = callResults.filter(r => r.error).length;

  yield put(TicketsViewActions.performBulkSiloChangeDone(successCount, failureCount, newSilo));
}

export function* performBulkSiloChangeDone(
  action: IActionWithPayload<string, { successCount: number; failureCount: number; silo: TicketSilo }>
): SagaIterator {
  const { successCount, failureCount, silo } = action.payload;

  yield put(
    EventTrackingActions.trackEvent("Bulk actions: Bulk silo change finished", EventPlace.Tickets, {
      successCount,
      failureCount,
    })
  );

  if (successCount > 0) {
    yield put(
      ToastsActions.createToast({
        variant: ToastVariant.Success,
        content: getToastContent(ToastContent.BULK_ACTION_SUCCESS, { count: successCount, silo }),
      })
    );
  }

  if (failureCount > 0) {
    yield put(
      ToastsActions.createToast({
        variant: ToastVariant.Error,
        content: getToastContent(ToastContent.BULK_ACTION_FAILURE, { count: failureCount, silo }),
      })
    );
  }

  yield put(TicketsViewActions.resetSelectedTickets());
}

export function* performBulkDelete(action: IActionWithPayload<string, { selectedIds: string[] }>): SagaIterator {
  const { selectedIds } = action.payload;
  const deleteCalls = [];
  const progressChannel = makeSubject();

  yield put(
    ToastsActions.createToast({
      id: "BULK_DELETE_PROGRESS_TOAST",
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.BULK_ACTION_PROGRESS, { progressChannel, count: selectedIds.length }),
      autoHideDelayTime: ToastAutoHideDelay.Infinite,
      removable: false,
    })
  );

  for (let i = 0; i < selectedIds.length; i += 1) {
    const ticketId = selectedIds[i];
    /* For parallel processing:
    deleteCalls.push(
      yield fork(function* () {
        const response = yield call(ApiManager.ticketApi.deleteTicket, ticketId);

        progressChannel.put({});
        return response;
      })
    );
    */

    const response = yield call(ApiManager.ticketApi.deleteTicket, ticketId);
    progressChannel(1, {});
    deleteCalls.push(response);
  }
  /* For parallel processing:
  const callResults = yield join(deleteCalls);
  */
  const callResults = deleteCalls;
  progressChannel(2);
  yield put(ToastsActions.removeToast({ id: "BULK_DELETE_PROGRESS_TOAST" }));

  const successCount = callResults.filter(r => r.result).length;
  const failureCount = callResults.filter(r => r.error).length;

  yield put(TicketsViewActions.performBulkDeleteDone(successCount, failureCount));
}

export function* performBulkDeleteDone(
  action: IActionWithPayload<string, { successCount: number; failureCount: number }>
): SagaIterator {
  const { successCount, failureCount } = action.payload;

  yield put(
    EventTrackingActions.trackEvent("Bulk actions: Bulk delete finished", EventPlace.Tickets, {
      successCount,
      failureCount,
    })
  );

  if (successCount > 0) {
    yield put(
      ToastsActions.createToast({
        variant: ToastVariant.Success,
        content: getToastContent(ToastContent.BULK_DELETE_SUCCESS, { count: successCount }),
      })
    );
  }

  if (failureCount > 0) {
    yield put(
      ToastsActions.createToast({
        variant: ToastVariant.Error,
        content: getToastContent(ToastContent.BULK_DELETE_FAILURE, { count: failureCount }),
      })
    );
  }
}

export function* performBulkActionDone(
  action: IActionWithPayload<string, { successCount: number; failureCount: number }>
): SagaIterator {
  const { successCount, failureCount } = action.payload;

  yield put(
    EventTrackingActions.trackEvent("Bulk actions: Bulk update finished", EventPlace.Tickets, {
      successCount,
      failureCount,
    })
  );

  if (successCount > 0) {
    yield put(
      ToastsActions.createToast({
        variant: ToastVariant.Success,
        content: getToastContent(ToastContent.BULK_ACTION_SUCCESS, { count: successCount }),
      })
    );
  }

  if (failureCount > 0) {
    yield put(
      ToastsActions.createToast({
        variant: ToastVariant.Error,
        content: getToastContent(ToastContent.BULK_ACTION_FAILURE, { count: failureCount }),
      })
    );
  }

  yield put(TicketsViewActions.resetSelectedTickets());
}

export function* mergeTickets(action: IActionWithPayload<string, IMergeTicketsPayload>): SagaIterator {
  const {
    payload: { parentTicketId, childTicketIds, onFinish },
  } = action;
  const updateCalls = [];
  const progressChannel = makeSubject();

  yield put(
    ToastsActions.createToast({
      id: "MERGE_TICKETS_PROGRESS_TOAST",
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.MERGE_TICKETS_PROGRESS, { progressChannel, count: childTicketIds.length }),
      autoHideDelayTime: ToastAutoHideDelay.Infinite,
      removable: false,
    })
  );

  for (let i = 0; i < childTicketIds.length; i += 1) {
    const ticketId = childTicketIds[i];

    const response = yield call(ApiManager.ticketApi.mergeTickets, parentTicketId, ticketId);
    progressChannel(1, {});
    updateCalls.push(response);
  }

  const callResults = updateCalls;
  progressChannel(2);
  yield put(ToastsActions.removeToast({ id: "MERGE_TICKETS_PROGRESS_TOAST" }));

  const successCount = callResults.filter(r => r.result).length;
  const failureCount = callResults.filter(r => r.error).length;

  yield put(TicketsViewActions.mergeTicketsDone(successCount, failureCount, onFinish));
}

export function* mergeTicketsDone(
  action: IActionWithPayload<string, { successCount: number; failureCount: number; onFinish(): () => void }>
): SagaIterator {
  const { successCount, failureCount, onFinish } = action.payload;

  yield put(
    EventTrackingActions.trackEvent("Merging tickets: merging finished", EventPlace.Tickets, {
      successCount,
      failureCount,
    })
  );

  if (successCount > 0) {
    yield put(
      ToastsActions.createToast({
        variant: ToastVariant.Success,
        content: getToastContent(ToastContent.MERGE_TICKETS_SUCCESS, { count: successCount }),
      })
    );
  }

  if (failureCount > 0) {
    yield put(
      ToastsActions.createToast({
        variant: ToastVariant.Error,
        content: getToastContent(ToastContent.MERGE_TICKETS_FAILURE, { count: failureCount }),
      })
    );
  }

  onFinish();
}

export function* refetchRecentTickets(): SagaIterator {
  const ticket = yield select(getTicket);
  const areRecentTicketsAvailable = yield select(selectRecentTicketsAvailable);

  if (!areRecentTicketsAvailable && ticket) {
    yield put(TicketActions.fetchRecentTickets({ ticketId: ticket.id, requesterEmail: ticket.requester.email }));
  }
}

/**
 * Resets the current sorting to the default. Runs every time the view is changed (a new search, silo, etc.)
 */
function* resetCurrentSorting(): SagaIterator {
  yield put(TicketsViewActions.resetCurrentSorting());
}

function* resetPage(): SagaIterator {
  yield put(TicketsViewActions.setPage(1));
}

export function* ticketsViewSagas(): SagaIterator {
  yield takeEvery(
    [
      TICKET.UPDATE_REQUESTER[RequestAction.SUCCESS],
      TICKET[CRUDAction.FETCH_SINGLE][RequestAction.SUCCESS],
      TICKET.UNSPAM[RequestAction.SUCCESS],
    ],
    refetchRecentTickets
  );
  yield takeEvery(TicketsViewActionsNames.TICKET_UPDATED, ticketUpdated);
  yield takeEvery(TicketsViewActionsNames.UPDATE_FILTER, updateFilter);
  yield takeEvery(TicketsViewActionsNames.SET_QUERY_PARAMS, setQueryParams);
  yield takeEvery(TicketsViewActionsNames.SET_VIEW, setView);
  yield takeEvery(TicketsViewActionsNames.UPDATE_TAGS, updateTags);
  yield takeEvery(TicketsViewActionsNames.UPDATE_PRIORITY, updatePriority);
  yield takeEvery(TicketsViewActionsNames.UPDATE_STATUS, updateStatus);
  yield takeEvery(TicketsViewActionsNames.UPDATE_SUBJECT, updateSubject);
  yield takeEvery(TicketsViewActionsNames.UPDATE_REQUESTER, updateRequester);
  yield takeEvery(TicketsViewActionsNames.UPDATE_CCS, updateCCs);
  yield takeEvery(TicketsViewActionsNames.UPDATE_ASSIGNEE, updateAssignee);
  yield takeEvery(TicketsViewActionsNames.UPDATE_ASSIGNED_TEAM, updateAssignedTeam);
  yield takeEvery(TicketsViewActionsNames.UPDATE_BATCH, updateBatch);
  yield takeEvery(TicketsViewActionsNames.UPDATE_BATCH_SUCCESS, updateBatchSuccess);
  yield takeEvery(TicketsViewActionsNames.UPDATE_BATCH_FAILURE, updateBatchFailure);
  yield takeEvery(TicketsViewActionsNames.CREATE_TICKET, createTicket);
  yield takeEvery(TICKET.CREATE[RequestAction.SUCCESS], createTicketSuccess);
  yield takeEvery(TICKET.CREATE[RequestAction.FAILURE], createTicketFailure);
  yield takeEvery(TicketsViewActionsNames.DELETE_TICKET, deleteTicket);
  yield takeEvery(TICKET.DELETE[RequestAction.SUCCESS], deleteTicketSuccess);
  yield takeEvery(TICKET.DELETE[RequestAction.FAILURE], deleteTicketFailure);
  yield takeEvery(TicketsViewActionsNames.SEND_MESSAGE, sendMessage);
  yield takeEvery(TicketsViewActionsNames.SET_INITIAL_ROUTE, setInitialRoute);
  yield takeEvery(TicketsViewActionsNames.SELECT_CUSTOM_VIEW, selectCustomView);
  yield takeEvery(TicketsViewActionsNames.SELECT_STANDARD_VIEW, selectStandardView);
  yield takeEvery(TicketsViewActionsNames.SET_SEARCH_QUERY, searchQueryChanged);
  yield takeEvery(TicketsViewActionsNames.CLEAR_SEARCH_QUERY, searchCleared);
  yield takeEvery(TicketsViewActionsNames.SAVE_DRAFT, saveDraft);
  yield takeEvery(TicketsViewActionsNames.CLEAR_DRAFT, clearDraft);
  yield takeEvery(TicketsViewActionsNames.UNSPAM, unspam);
  yield takeEvery(TicketsViewActionsNames.COPY_TICKET_URL, copyTicketUrl);
  yield takeEvery(TicketsViewActionsNames.COPY_REQUESTER_EMAIL, copyRequesterEmail);
  yield takeEvery(TicketsViewActionsNames.SET_CURRENT_ITEM, setCurrentItem);
  yield takeEvery(TicketsViewActionsNames.SHOW_FROM_ALL_TEAMS, showFromAllTeams);
  yield takeEvery(
    [
      TicketsViewActionsNames.UPDATE_FILTER,
      TicketsViewActionsNames.PERFORM_BULK_SILO_CHANGE_DONE,
      TicketsViewActionsNames.PERFORM_BULK_DELETE_DONE,
      TicketsViewActionsNames.SET_VIEW,
      TicketsViewActionsNames.CLEAR_SEARCH_QUERY,
      TicketsViewActionsNames.SET_INITIAL_ROUTE,
      TicketsViewActionsNames.UPDATE_BATCH_SUCCESS,
      TicketsViewActionsNames.SET_PAGE,
      TicketsViewActionsNames.REFRESH_TICKETS,
      TicketsViewActionsNames.SET_SILO,
      TicketsViewActionsNames.UPDATE_CURRENT_SORTING,
      TicketsViewActionsNames.RESET_CURRENT_SORTING,
    ],
    fetchTickets
  );
  yield takeEvery(
    [
      TicketsViewActionsNames.SET_SILO,
      TicketsViewActionsNames.SET_SEARCH_QUERY,
      TicketsViewActionsNames.CLEAR_SEARCH_QUERY,
    ],
    resetCurrentSorting
  );
  yield takeEvery(
    [TicketsViewActionsNames.RESET_CURRENT_SORTING, TicketsViewActionsNames.UPDATE_CURRENT_SORTING],
    resetPage
  );
  yield takeEvery(TicketsViewActionsNames.FETCH_SINGLE, fetchSingle);
  yield takeEvery(TicketsViewActionsNames.FETCH_CLOSEST_TICKET, fetchClosestTicket);
  yield takeEvery(TICKET.FETCH_CLOSEST_TICKET[RequestAction.SUCCESS], fetchClosestTicketSuccess);
  yield takeEvery(TICKET[CRUDAction.FETCH_SINGLE][RequestAction.FAILURE], ticketFetchFailed);
  yield takeEvery(TICKET.UPDATE_SUBJECT[RequestAction.SUCCESS], updateSubjectSuccess);
  yield takeEvery(TICKET.UPDATE_REQUESTER[RequestAction.SUCCESS], updateRequesterSuccess);
  yield takeEvery(TICKET.UPDATE_REQUESTER[RequestAction.FAILURE], updateRequesterFailure);
  yield takeEvery(
    [TICKET.UPDATE_TAGS[RequestAction.FAILURE], TICKET.ADD_TAGS[RequestAction.FAILURE]],
    ticketTagLimitReachedFailure
  );
  yield takeEvery(TICKET.UPDATE_CCS[RequestAction.SUCCESS], updateCCsSuccess);
  yield takeEvery(TICKET.UPDATE_CCS[RequestAction.FAILURE], updateCCsFailure);
  yield takeEvery(TICKET.SUMMARIZE_TICKET[RequestAction.SUCCESS], summarizeTicketSuccess);
  yield takeEvery(TICKET.SUMMARIZE_TICKET[RequestAction.FAILURE], summarizeTicketFailure);
  yield takeEvery(TICKET.APPLY_MACRO[RequestAction.SUCCESS], applyMacroSuccess);
  yield takeEvery(TICKET.APPLY_MACRO[RequestAction.FAILURE], applyMacroFailure);
  yield takeLatest(TICKET.UPDATE_CUSTOM_FIELD[RequestAction.SUCCESS], updateCustomFieldSuccess);
  yield takeLatest(TICKET.UPDATE_CUSTOM_FIELD[RequestAction.FAILURE], updateCustomFieldFailure);
  yield takeLatest(TICKET.UPDATE_SILO[RequestAction.SUCCESS], updateSiloSuccess);
  yield takeLatest(TICKET.UPDATE_SILO[RequestAction.FAILURE], updateSiloFailure);
  yield takeEvery(TICKET.UPDATE_ASSIGNEE[RequestAction.SUCCESS], updateAssigneeSuccess);
  yield takeEvery(TICKET.UPDATE_ASSIGNEE[RequestAction.FAILURE], updateAssigneeFailure);
  yield takeEvery(TICKET.UPDATE_ASSIGNED_TEAM[RequestAction.SUCCESS], updateTeamsSuccess);
  yield takeEvery(TICKET.UPDATE_ASSIGNED_TEAM[RequestAction.FAILURE], updateTeamsFailure);
  yield takeEvery(TICKET.UPDATE_PRIORITY[RequestAction.SUCCESS], updatePrioritySuccess);
  yield takeEvery(TICKET.UPDATE_PRIORITY[RequestAction.FAILURE], updatePriorityFailure);
  yield takeEvery(TICKET.UPDATE_STATUS[RequestAction.SUCCESS], updateStatusSuccess);
  yield takeEvery(TICKET.UPDATE_STATUS[RequestAction.FAILURE], updateStatusFailure);
  yield takeEvery(TICKET.SEND_MESSAGE[RequestAction.SUCCESS], sendMessageSuccess);
  yield takeEvery(TICKET.SEND_MESSAGE[RequestAction.FAILURE], sendMessageFailure);
  yield takeEvery(TICKET.UNSPAM[RequestAction.SUCCESS], unspamSuccess);
  yield takeEvery(TICKET.UNSPAM[RequestAction.FAILURE], unspamFailure);

  yield takeEvery(TicketsViewActionsNames.UPDATE_CUSTOM_VIEW, updateCustomView);
  yield takeEvery(TicketsViewActionsNames.CREATE_CUSTOM_VIEW, createCustomView);
  yield takeEvery(TicketsViewActionsNames.FETCH_ORIGINAL_MESSAGE, fetchOriginalMessage);
  yield takeEvery(TicketsViewActionsNames.FETCH_ORIGINAL_MESSAGE_FAILURE, fetchOriginalMessageFailure);
  yield takeEvery(TicketsViewActionsNames.SYNC_DELETE, syncDelete);

  yield takeEvery(TicketsViewActionsNames.ADD_FOLLOWER, addFollower);
  yield takeEvery(TicketsViewActionsNames.DELETE_FOLLOWER, deleteFollower);

  yield takeEvery(TicketsViewActionsNames.DELETE_FOLLOWER_SUCCESS, deleteFollowerSuccess);
  yield takeEvery(TicketsViewActionsNames.DELETE_FOLLOWER_FAILURE, deleteFollowerFailure);

  yield takeEvery(TicketsViewActionsNames.ADD_FOLLOWER_SUCCESS, addFollowerSuccess);
  yield takeEvery(TicketsViewActionsNames.ADD_FOLLOWER_FAILURE, addFollowerFailure);
  yield takeEvery(TicketsViewActionsNames.PERFORM_BULK_ACTION, performBulkAction);
  yield takeEvery(TicketsViewActionsNames.PERFORM_BULK_ACTION_DONE, performBulkActionDone);
  yield takeEvery(TicketsViewActionsNames.PERFORM_BULK_DELETE, performBulkDelete);
  yield takeEvery(TicketsViewActionsNames.PERFORM_BULK_DELETE_DONE, performBulkDeleteDone);
  yield takeEvery(TicketsViewActionsNames.PERFORM_BULK_SILO_CHANGE, performBulkSiloChange);
  yield takeEvery(TicketsViewActionsNames.PERFORM_BULK_SILO_CHANGE_DONE, performBulkSiloChangeDone);
  yield takeEvery(TicketsViewActionsNames.MERGE_TICKETS_DONE, mergeTicketsDone);
  yield takeEvery(TicketsViewActionsNames.MERGE_TICKETS, mergeTickets);
  yield takeEvery(TicketsViewActionsNames.FETCH_UNSTRIPPED_MESSAGE, fetchUnstrippedMessage);
  yield takeEvery(TicketsViewActionsNames.FETCH_UNSTRIPPED_MESSAGE_FAILURE, fetchUnstrippedMessageFailure);
}
